import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { Printer, FileDown } from 'lucide-react';
import DateRangeFilter from './DateRangeFilter';
import { exportCSV, inRange } from './reportUtils';
import { useCompanyInfo, generatePrintHTML, openPrint } from './ReportExporter';

// Settlement detail scoped to Company Drivers (W2) -- the "User" side of
// payroll, as opposed to Owner Operators who are their own sub-carrier
// (see Carrier Settlements / Carrier Pay).
export default function UserSettlementsReport() {
  const [settlements, setSettlements] = useState([]);
  const [loads, setLoads] = useState([]);
  const [driverProfiles, setDriverProfiles] = useState([]);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const company = useCompanyInfo();

  useEffect(() => {
    Promise.all([
      client.entities.Settlement.list('-settlement_date', 500),
      client.entities.Load.list('-delivery_date', 500),
      client.entities.DriverProfile.list('full_name', 200),
    ]).then(([s, l, d]) => { setSettlements(s); setLoads(l); setDriverProfiles(d); setLoading(false); });
  }, []);

  const companyDriverEmails = new Set(
    driverProfiles.filter(d => d.driver_type === 'Company Driver' || !d.driver_type).map(d => d.user_email)
  );

  const filtered = settlements.filter(s =>
    inRange(s.settlement_date || s.finalized_date || s.created_date, start, end) &&
    companyDriverEmails.has(s.driver_email)
  );

  const getLoad = (id) => loads.find(l => l.id === id);

  const totals = {
    gross: filtered.reduce((s, r) => s + (r.gross_revenue || 0), 0),
    net: filtered.reduce((s, r) => s + (r.final_net_pay ?? r.net_driver_pay ?? 0), 0),
  };

  const csvRows = filtered.map(s => {
    const l = getLoad(s.load_id);
    return {
      Driver: s.driver_name || '',
      'Load #': s.load_number || '',
      'Delivery Date': l?.delivery_date || '',
      Gross: (s.gross_revenue || 0).toFixed(2),
      'Net Pay': (s.final_net_pay ?? s.net_driver_pay ?? 0).toFixed(2),
      Status: s.status || '',
    };
  });

  const handlePrint = () => {
    const cols = ['Driver', 'Load #', 'Delivery Date', 'Gross', 'Net Pay', 'Status'];
    const dataRows = filtered.map(s => {
      const l = getLoad(s.load_id);
      return [
        { value: s.driver_name || '', class: 'bold' }, `#${s.load_number || ''}`, l?.delivery_date || '—',
        { value: `$${(s.gross_revenue || 0).toLocaleString()}`, class: 'positive' },
        { value: `$${(s.final_net_pay ?? s.net_driver_pay ?? 0).toFixed(2)}`, class: 'positive' },
        s.status || '',
      ];
    });
    const totalsRow = [{ value: `TOTALS (${filtered.length})`, class: '' }, '', '', { value: `$${totals.gross.toLocaleString()}`, class: 'gold' }, { value: `$${totals.net.toFixed(2)}`, class: 'gold' }, ''];
    const cards = [
      { label: 'Settlements', value: filtered.length, color: 'gold' },
      { label: 'Gross Revenue', value: `$${totals.gross.toLocaleString()}`, color: 'positive' },
      { label: 'Net Driver Pay', value: `$${totals.net.toFixed(2)}`, color: 'positive' },
    ];
    const html = generatePrintHTML('User Settlements Report (Company Drivers)', `${start || 'All'} – ${end || 'All'}`, cols, dataRows, totalsRow, company, cards);
    openPrint(html);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <DateRangeFilter onChange={(s, e) => { setStart(s); setEnd(e); }} onExport={() => exportCSV(csvRows, 'user-settlements.csv')} exportDisabled={loading || filtered.length === 0} />
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><Printer className="w-4 h-4" /> Print</button>
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><FileDown className="w-4 h-4" /> Export PDF</button>
      </div>
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {['Driver', 'Load #', 'Delivery Date', 'Gross', 'Net Pay', 'Status'].map(h => (
                <th key={h} className="text-left px-3 py-2.5 text-muted-foreground font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map(s => {
              const l = getLoad(s.load_id);
              return (
                <tr key={s.id} className="hover:bg-white/3">
                  <td className="px-3 py-2.5 font-medium text-foreground">{s.driver_name}</td>
                  <td className="px-3 py-2.5 text-primary">#{s.load_number}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">{l?.delivery_date || '—'}</td>
                  <td className="px-3 py-2.5 font-semibold text-foreground">${(s.gross_revenue || 0).toLocaleString()}</td>
                  <td className="px-3 py-2.5 font-bold text-green-400">${(s.final_net_pay ?? s.net_driver_pay ?? 0).toFixed(2)}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">{s.status}</td>
                </tr>
              );
            })}
          </tbody>
          {filtered.length > 0 && (
            <tfoot>
              <tr className="border-t border-border bg-white/5 font-bold">
                <td colSpan={3} className="px-3 py-2.5 text-foreground">TOTALS ({filtered.length})</td>
                <td className="px-3 py-2.5 text-foreground">${totals.gross.toLocaleString()}</td>
                <td className="px-3 py-2.5 text-green-400">${totals.net.toFixed(2)}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
        {filtered.length === 0 && !loading && <p className="text-center text-muted-foreground py-8 text-sm">No company driver settlements found for this period.</p>}
        {loading && <p className="text-center text-muted-foreground py-8 text-sm">Loading...</p>}
      </div>
    </div>
  );
}
